import { useMemo } from 'react';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { getDeviceStatusCode } from 'utils/devices/icon.utils';

const EMPTY_RESULTS = [];

const useMaintenanceGroups = () => {
  const { data: connectedDevices = EMPTY_RESULTS, isLoading: connectedDevicesLoading } =
    useGetConnectedDevicesQuery();
  const { data: deviceGroups = EMPTY_RESULTS, isLoading: deviceGroupsLoading } =
    useGetGroupsQuery();

  const isLoading = Boolean(connectedDevicesLoading || deviceGroupsLoading);

  const groups = useMemo(() => {
    if (!deviceGroups.length) return [];

    const groupDefinitions = deviceGroups.filter((group) => group.id !== 36);
    const groupedDeviceIds = new Set(groupDefinitions.flatMap((group) => group.devices ?? []));
    const unassignedDeviceIds = connectedDevices
      .filter((device) => !groupedDeviceIds.has(device.id))
      .map((device) => device.id);

    if (unassignedDeviceIds.length > 0) {
      groupDefinitions.push({
        id: '',
        name: 'Misc. Devices',
        type: '',
        devices: unassignedDeviceIds,
      });
    }

    return groupDefinitions.map((initialGroup) => {
      if (!initialGroup?.devices?.length) return initialGroup;

      const groupDevices = (initialGroup.devices ?? [])
        .map((deviceId) => {
          const device = connectedDevices?.find((d) => d.id === deviceId);
          if (!device) return null;

          return {
            id: device.id,
            alias: device.alias,
            name: device.name,
            device_type: device.device_type,
            status: getDeviceStatusCode(device),
            acPowerFail: device.data?.[0]?.ACPowerFail ?? null,
            batteryFail: device.data?.[0]?.BatteryFail ?? null,
          };
        })
        .filter(Boolean);

      const status = groupDevices.reduce(
        (acc, value) => (acc.includes(value.status) ? acc : [...acc, value.status]),
        [],
      );

      const acPowerFail = groupDevices.some((d) => d.acPowerFail === 'Fail') ? 'Fail' : 'Normal';
      const batteryFail = groupDevices.some((d) => d.batteryFail === 'Fail') ? 'Fail' : 'Normal';

      return {
        id: initialGroup.id,
        name: initialGroup.name,
        type: initialGroup.type,
        devices: groupDevices,
        deviceCount: groupDevices.length,
        status,
        acPowerFail,
        batteryFail,
      };
    });
  }, [connectedDevices, deviceGroups]);

  return { groups, isLoading };
};

export default useMaintenanceGroups;
